import React, { useEffect, useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform, animate } from 'framer-motion';

const EASE = [0.16, 1, 0.3, 1];
const BANDS = 5;

export default function SliceRevealLogo({ animate: isAnimating, parallaxEnabled = false }) {
  const containerRef = useRef(null);
  const turbScale = useMotionValue(60);

  useEffect(() => {
    if (!isAnimating) return;
    const controls = animate(turbScale, 0, { duration: 1.4, ease: EASE });
    return () => controls.stop();
  }, [isAnimating, turbScale]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(useTransform(mx, [-1, 1], [-8, 8]), { stiffness: 80, damping: 30 });
  const sy = useSpring(useTransform(my, [-1, 1], [-8, 8]), { stiffness: 80, damping: 30 });

  useEffect(() => {
    if (!parallaxEnabled) return;
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(pointer: coarse)').matches) return;

    const onMove = (e) => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      mx.set((e.clientX / w) * 2 - 1);
      my.set((e.clientY / h) * 2 - 1);
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, [parallaxEnabled, mx, my]);

  return (
    <motion.div
      ref={containerRef}
      style={{
        position: 'relative',
        width: 'min(840px, 88vw)',
        x: sx,
        y: sy,
      }}
    >
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <defs>
          <filter id="hero-turbulence" x="-20%" y="-20%" width="140%" height="140%">
            <feTurbulence type="fractalNoise" baseFrequency="0.012" numOctaves="2" seed="3" />
            <motion.feDisplacementMap in="SourceGraphic" scale={turbScale} />
          </filter>
        </defs>
      </svg>

      <div style={{ filter: 'url(#hero-turbulence)' }}>
        {Array.from({ length: BANDS }).map((_, i) => {
          const top = (i / BANDS) * 100;
          const bottom = ((BANDS - 1 - i) / BANDS) * 100;
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              animate={isAnimating ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              transition={{ delay: i * 0.08, duration: 0.9, ease: EASE }}
              style={{
                position: i === 0 ? 'relative' : 'absolute',
                inset: i === 0 ? undefined : 0,
                clipPath: `inset(${top}% 0 ${bottom}% 0)`,
                width: '100%',
                pointerEvents: 'none',
              }}
            >
              <img
                src="/tsangalogosansfond.png"
                alt={i === 0 ? 'TSANGA' : ''}
                aria-hidden={i !== 0}
                style={{ display: 'block', width: '100%', height: 'auto', mixBlendMode: 'screen' }}
              />
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}
